import {formatLocationDisplay} from '../../utils/locationDisplay';

export const DEFAULT_DRIVER_STATUS_FILTER = 'online';

export const DRIVER_STATUS_FILTERS = [
    ['all', 'All'],
    ['online', 'Online'],
    ['available', 'Available'],
    ['busy', 'On delivery'],
    ['no-location', 'No GPS'],
];

const asNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

export const isNaviWhitelabelRecord = (record) => {
    if (!record || !Object.prototype.hasOwnProperty.call(record, 'whitelabel_id')) return false;

    return record.whitelabel_id === null || String(record.whitelabel_id) === '1';
};

export const getNaviDispatchPickups = (pickups = []) => pickups.filter(isNaviWhitelabelRecord);

export const isScheduledPickup = (pickup) => Boolean(pickup?.scheduled_pickup_time);

export const togglePickupSelection = (selectedPickupIds = [], pickupId) => {
    const pickupIdKey = String(pickupId);
    const alreadySelected = selectedPickupIds.some((selectedId) => String(selectedId) === pickupIdKey);

    return alreadySelected
        ? selectedPickupIds.filter((selectedId) => String(selectedId) !== pickupIdKey)
        : [...selectedPickupIds, pickupId];
};

const sanitizeNaviVehicle = (vehicle) => {
    if (!isNaviWhitelabelRecord(vehicle)) return null;

    const sanitized = {...vehicle};
    if (Object.prototype.hasOwnProperty.call(vehicle, 'truck_type')) {
        sanitized.truck_type = isNaviWhitelabelRecord(vehicle.truck_type) ? vehicle.truck_type : null;
    }
    if (Object.prototype.hasOwnProperty.call(vehicle, 'truckType')) {
        sanitized.truckType = isNaviWhitelabelRecord(vehicle.truckType) ? vehicle.truckType : null;
    }

    return sanitized;
};

export const toMapPosition = (value) => {
    if (!value) return null;

    const latitude = asNumber(value.latitude ?? value.lat);
    const longitude = asNumber(value.longitude ?? value.lng ?? value.long);

    if (latitude === null || longitude === null || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
        return null;
    }

    return {lat: latitude, lng: longitude};
};

const MARKER_COORDINATE_PRECISION = 6;
const MAX_MARKER_STACK_SPREAD = 15;

const getMarkerStackPriority = (marker) => (
    (marker.anchor ? 100 : 0)
    + (marker.type === 'driver' ? 20 : marker.type === 'delivery' ? 10 : 0)
);

const getMarkerCoordinateKey = (position, precision) => (
    `${position.lat.toFixed(precision)}:${position.lng.toFixed(precision)}`
);

export const getOverlappingMarkerOffsets = (
    markers = [],
    markerPeek = 5,
    precision = MARKER_COORDINATE_PRECISION
) => {
    const coordinatePrecision = Math.min(Math.max(Math.floor(asNumber(precision) ?? MARKER_COORDINATE_PRECISION), 0), 10);
    const requestedPeek = Math.max(asNumber(markerPeek) ?? 5, 0);
    const groups = new Map();
    const offsets = {};

    markers.forEach((marker) => {
        const position = toMapPosition(marker?.position);
        if (marker?.key === null || marker?.key === undefined || !position) return;

        const coordinateKey = getMarkerCoordinateKey(position, coordinatePrecision);
        if (!groups.has(coordinateKey)) groups.set(coordinateKey, []);
        groups.get(coordinateKey).push(marker);
    });

    groups.forEach((group) => {
        const stack = [...group].sort((first, second) => (
            getMarkerStackPriority(second) - getMarkerStackPriority(first)
            || String(first.key).localeCompare(String(second.key))
        ));
        const markerPeekSize = stack.length > 1
            ? Math.min(requestedPeek, MAX_MARKER_STACK_SPREAD / (stack.length - 1))
            : 0;

        stack.forEach((marker, index) => {
            const stackOffset = Number((index * markerPeekSize).toFixed(2));
            offsets[marker.key] = {x: stackOffset, y: stackOffset};
        });
    });

    return offsets;
};

const uniqueLocations = (locations) => {
    const seen = new Set();

    return locations.filter(Boolean).filter((location) => {
        const key = `${location.position.lat.toFixed(6)}:${location.position.lng.toFixed(6)}`;

        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

export const getPickupLocations = (pickup) => {
    if (!pickup) return {origins: [], dropoffs: []};

    const itemOrigins = (pickup.items || []).map((item) => item?.origin);
    const itemDropoffs = (pickup.items || []).map((item) => item?.dropoff);
    const originSources = pickup.origins?.length
        ? pickup.origins
        : [...itemOrigins, {
            latitude: pickup.origin_lat,
            longitude: pickup.origin_long,
            address: pickup.origin_address,
            place_name: pickup.origin_place_name ?? pickup.pickup_origin_place_name,
        }];
    const dropoffSources = pickup.dropoffs?.length
        ? pickup.dropoffs
        : [...itemDropoffs, {
            latitude: pickup.dropoff_lat,
            longitude: pickup.dropoff_long,
            address: pickup.dropoff_address,
            place_name: pickup.dropoff_place_name ?? pickup.pickup_dropoff_place_name,
        }];

    const normalize = (location, index) => {
        const position = toMapPosition(location);
        if (!position) return null;

        return {
            ...location,
            id: location?.id ?? index,
            address: location?.address || '',
            displayAddress: formatLocationDisplay(location),
            position,
        };
    };

    return {
        origins: uniqueLocations(originSources.map(normalize)),
        dropoffs: uniqueLocations(dropoffSources.map(normalize)),
    };
};

export const getTrackingTarget = (pickup) => {
    if (!pickup) return null;

    const locations = getPickupLocations(pickup);
    const incomplete = (location) => (
        !location.completed_at
        && !['completed', 'complete'].includes(String(location.status || '').toLowerCase())
    );
    const incompleteOrigin = locations.origins.find(incomplete);
    const incompleteDropoff = locations.dropoffs.find(incomplete);
    const deliveryStatusId = Number(pickup.delivery_status?.id ?? pickup.delivery_status_id);

    if (deliveryStatusId >= 3) {
        return (incompleteDropoff || locations.dropoffs[0])?.position || null;
    }

    return (incompleteOrigin || incompleteDropoff || locations.origins[0] || locations.dropoffs[0])?.position || null;
};

export const getDriverLocation = (driver) => {
    const source = driver?.current_location || driver?.driver_location || driver?.location;
    const position = toMapPosition(source);

    if (!position) return null;

    return {
        ...position,
        updatedAt: source?.updated_at || source?.updatedAt || null,
    };
};

export const normalizeDriverLocations = (locations = []) => locations.reduce((normalized, location) => {
    const driverId = location?.driver_id ?? location?.driver?.id;
    const position = toMapPosition(location);

    if (driverId === null || driverId === undefined || driverId === '' || !position) return normalized;

    normalized[driverId] = {
        ...position,
        updatedAt: location?.updated_at || location?.updatedAt || null,
    };
    return normalized;
}, {});

const getLocationTimestamp = (location) => {
    if (!location?.updatedAt) return null;
    const timestamp = typeof location.updatedAt === 'number'
        ? location.updatedAt
        : new Date(location.updatedAt).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
};

export const mergeDriverLocations = (current = {}, incoming = {}) => Object.entries(incoming)
    .reduce((merged, [driverId, location]) => {
        const existing = merged[driverId];
        const existingTimestamp = getLocationTimestamp(existing);
        const incomingTimestamp = getLocationTimestamp(location);

        if (!existing || existingTimestamp === null || (
            incomingTimestamp !== null && incomingTimestamp >= existingTimestamp
        )) {
            merged[driverId] = location;
        }

        return merged;
    }, {...current});

export const getDriverInitials = (name = '') => {
    const initials = name
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');

    return initials || '?';
};

const mergeTruckTypeMetadata = (truckTypes, supplementalTruckTypes = []) => {
    const supplementalById = supplementalTruckTypes
        .filter((truckType) => (
            truckType?.id !== undefined
            && truckType?.id !== null
            && isNaviWhitelabelRecord(truckType)
        ))
        .reduce((typesById, truckType) => {
            const id = String(truckType.id);
            if (!typesById.has(id)) typesById.set(id, truckType);
            return typesById;
        }, new Map());
    const typesById = new Map();

    truckTypes
        .filter((truckType) => (
            truckType?.id !== undefined
            && truckType?.id !== null
            && isNaviWhitelabelRecord(truckType)
        ))
        .forEach((truckType) => {
            const id = String(truckType.id);
            const supplemental = supplementalById.get(id) || {};
            const existing = typesById.get(id) || supplemental;

            typesById.set(id, {
                ...supplemental,
                ...existing,
                ...truckType,
                type: truckType.type || existing.type || supplemental.type,
                icon: truckType.icon || existing.icon || supplemental.icon,
                title: truckType.title || existing.title || supplemental.title,
            });
        });

    return Array.from(typesById.values());
};

export const getDriverTruckTypes = (driver) => {
    const hasVehicles = Object.prototype.hasOwnProperty.call(driver || {}, 'vehicles');
    const hasTruckTypes = Object.prototype.hasOwnProperty.call(driver || {}, 'truck_types');
    const legacyTruckType = driver?.truck_type;

    if (hasVehicles) {
        const fromVehicles = Array.isArray(driver?.vehicles)
            ? driver.vehicles
                .filter(isNaviWhitelabelRecord)
                .map((vehicle) => vehicle?.truck_type || vehicle?.truckType)
            : [];
        const supplemental = [
            ...(Array.isArray(driver?.truck_types) ? driver.truck_types : []),
            legacyTruckType,
        ];

        return mergeTruckTypeMetadata(fromVehicles, supplemental);
    }

    if (hasTruckTypes) {
        return mergeTruckTypeMetadata(
            Array.isArray(driver?.truck_types) ? driver.truck_types : [],
            [legacyTruckType]
        );
    }

    return mergeTruckTypeMetadata([legacyTruckType]);
};

export const getDriverAvailability = (driver, assignedPickup) => {
    const statusId = Number(assignedPickup?.delivery_status?.id ?? assignedPickup?.delivery_status_id);
    const inProgress = assignedPickup && (
        (statusId >= 1 && statusId <= 4)
        || (assignedPickup.started_at && !assignedPickup.completed_at && !assignedPickup.canceled_at)
    );

    if (inProgress) return 'busy';
    if (Boolean(Number(driver?.available_to_deliver)) || driver?.available_to_deliver === true) return 'available';
    return 'unavailable';
};

export const isCompanyEmployeeDriver = (driver) => (
    Number(driver?.type) === 3
    && Boolean(driver?.company_id ?? driver?.company?.id)
);

export const isDispatchDriver = (driver) => {
    if (!isNaviWhitelabelRecord(driver) || Number(driver.type) === 2) return false;

    return Number(driver.admin_approved) === 1 || driver.admin_approved === true;
};

export const getDriverLocationSubscriptionIds = (drivers = []) => Array.from(new Set(
    drivers
        .filter(isDispatchDriver)
        .map((driver) => driver?.id)
        .filter((driverId) => driverId !== null && driverId !== undefined && driverId !== '')
        .map(String)
)).sort((firstId, secondId) => firstId.localeCompare(secondId, undefined, {numeric: true}));

export const normalizeDriverLocationUpdate = (event, fallbackDriverId = null) => {
    const driverId = event?.driver_id ?? fallbackDriverId;
    const position = toMapPosition(event);

    if (driverId === null || driverId === undefined || driverId === '' || !position) return null;
    if (event?.driver_id != null && fallbackDriverId != null && String(driverId) !== String(fallbackDriverId)) {
        return null;
    }

    const reportedAt = event?.updated_at || event?.updatedAt;
    return {
        driverId: String(driverId),
        location: {
            ...position,
            // Receiving a cached position never makes its GPS measurement newer.
            updatedAt: getTimestamp(reportedAt) === null ? null : reportedAt,
        },
    };
};

const getTimestamp = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const timestamp = typeof value === 'number' ? value : new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
};

export const DRIVER_ONLINE_THRESHOLD_MS = 2 * 60 * 1000;

export const normalizeDriverPresenceUpdate = (event, fallbackDriverId = null) => {
    const driverId = event?.driver_id ?? fallbackDriverId;
    if (driverId === null || driverId === undefined || driverId === '') return null;
    if (event?.driver_id != null && fallbackDriverId != null && String(driverId) !== String(fallbackDriverId)) {
        return null;
    }
    const lastSeenAt = event?.last_seen_at;
    const sharingEnabled = event?.location_sharing_enabled;
    if (getTimestamp(lastSeenAt) === null || typeof sharingEnabled !== 'boolean') return null;
    return {driverId: String(driverId), presence: {lastSeenAt, sharingEnabled}};
};

export const normalizeDriverPresence = (records = []) => records.reduce((normalized, record) => {
    const update = normalizeDriverPresenceUpdate(record, record?.driver?.id ?? record?.id);
    if (update) normalized[update.driverId] = update.presence;
    return normalized;
}, {});

export const mergeDriverPresence = (current = {}, incoming = {}) => Object.entries(incoming)
    .reduce((merged, [driverId, presence]) => {
        const existingTimestamp = getTimestamp(merged[driverId]?.lastSeenAt);
        const incomingTimestamp = getTimestamp(presence?.lastSeenAt);
        if (incomingTimestamp !== null && (existingTimestamp === null || incomingTimestamp >= existingTimestamp)) {
            merged[driverId] = presence;
        }
        return merged;
    }, {...current});

export const isDriverOnline = (presence, now = Date.now()) => {
    const lastSeenAt = getTimestamp(presence?.lastSeenAt);
    return presence?.sharingEnabled === true && lastSeenAt !== null
        && now - lastSeenAt < DRIVER_ONLINE_THRESHOLD_MS;
};

export const getDriverConnectionLabel = (presence, now = Date.now()) => {
    if (presence?.sharingEnabled === false) return 'Sharing off';
    if (isDriverOnline(presence, now)) return 'Online';
    if (getTimestamp(presence?.lastSeenAt) !== null) return `Last seen ${formatRelativeTime(presence.lastSeenAt, now)}`;
    return 'Connection unknown';
};

export const getDriverLocationLabel = (location, now = Date.now()) => {
    if (!toMapPosition(location)) return 'No GPS';
    if (getTimestamp(location?.updatedAt) === null) return 'GPS update time unknown';
    return `Location updated ${formatRelativeTime(location.updatedAt, now).toLowerCase()}`;
};

export const sanitizeNaviWhitelabelDriver = (driver) => {
    if (!isNaviWhitelabelRecord(driver)) return null;

    const sanitized = {...driver};
    if (Object.prototype.hasOwnProperty.call(driver, 'company')) {
        sanitized.company = isNaviWhitelabelRecord(driver.company) ? driver.company : null;
    }
    if (Object.prototype.hasOwnProperty.call(driver, 'truck_type')) {
        sanitized.truck_type = isNaviWhitelabelRecord(driver.truck_type) ? driver.truck_type : null;
    }
    if (Array.isArray(driver.truck_types)) {
        sanitized.truck_types = driver.truck_types.filter(isNaviWhitelabelRecord);
    }
    if (Array.isArray(driver.vehicles)) {
        sanitized.vehicles = driver.vehicles.map(sanitizeNaviVehicle).filter(Boolean);
    }

    return sanitized;
};

export const isDriverCompatible = (driver, pickup) => {
    if (!isNaviWhitelabelRecord(driver) || !isNaviWhitelabelRecord(pickup) || Number(driver.type) === 2) return false;

    if (pickup.truck_type && !isNaviWhitelabelRecord(pickup.truck_type)) return false;

    const pickupTruckTypeId = pickup.truck_type?.id ?? pickup.truck_type_id;
    if (pickupTruckTypeId === undefined || pickupTruckTypeId === null) return true;

    return getDriverTruckTypes(driver).some((truckType) => String(truckType.id) === String(pickupTruckTypeId));
};

export const sanitizeNaviWhitelabelPickup = (pickup) => {
    if (!isNaviWhitelabelRecord(pickup)) return null;

    const sanitized = {...pickup};
    if (Object.prototype.hasOwnProperty.call(pickup, 'user')) {
        sanitized.user = isNaviWhitelabelRecord(pickup.user) ? pickup.user : null;
    }
    if (Object.prototype.hasOwnProperty.call(pickup, 'driver')) {
        const driver = sanitizeNaviWhitelabelDriver(pickup.driver);
        sanitized._dispatch_has_assigned_driver = Boolean(
            pickup.driver || pickup.has_assigned_driver
        );
        sanitized.driver = driver && isDispatchDriver(driver) ? driver : null;
    } else if (Object.prototype.hasOwnProperty.call(pickup, 'has_assigned_driver')) {
        sanitized._dispatch_has_assigned_driver = Boolean(pickup.has_assigned_driver);
    }
    if (Object.prototype.hasOwnProperty.call(pickup, 'cancelled_by_driver')) {
        const cancelledByDriver = sanitizeNaviWhitelabelDriver(pickup.cancelled_by_driver);
        sanitized.cancelled_by_driver = cancelledByDriver && isDispatchDriver(cancelledByDriver)
            ? cancelledByDriver
            : null;
    }
    if (Object.prototype.hasOwnProperty.call(pickup, 'truck_type')) {
        sanitized.truck_type = isNaviWhitelabelRecord(pickup.truck_type) ? pickup.truck_type : null;
    }
    if (Object.prototype.hasOwnProperty.call(pickup, 'vehicle')) {
        sanitized.vehicle = sanitizeNaviVehicle(pickup.vehicle);
    }

    return sanitized;
};

export const hasPickupDriver = (pickup) => Boolean(
    pickup?.driver || pickup?._dispatch_has_assigned_driver || pickup?.has_assigned_driver
);

export const getDriverAssignedPickups = (pickups = [], driverId) => {
    if (driverId === null || driverId === undefined || driverId === '') return [];

    const driverIdKey = String(driverId);
    return pickups.filter((pickup) => (
        pickup?.driver?.id !== null
        && pickup?.driver?.id !== undefined
        && String(pickup.driver.id) === driverIdKey
    ));
};

export const getSelectedAssignedPickups = (assignedPickups = [], selectedPickups = []) => {
    const selectedPickupIdSet = new Set(selectedPickups.map((pickup) => String(pickup?.id)));

    return assignedPickups.filter((pickup) => selectedPickupIdSet.has(String(pickup?.id)));
};

export const getDriverPickupSelection = (pickups = [], driverId, currentPickupId = null) => {
    const assignedPickups = getDriverAssignedPickups(pickups, driverId);
    if (assignedPickups.length) {
        const currentPickup = pickups.find(
            (pickup) => String(pickup.id) === String(currentPickupId)
        );
        const currentAssignedPickup = assignedPickups.find(
            (pickup) => String(pickup.id) === String(currentPickupId)
        );
        return {
            assignedPickups,
            pickupId: currentPickup && !hasPickupDriver(currentPickup)
                ? currentPickup.id
                : currentAssignedPickup?.id ?? assignedPickups[0].id,
            source: 'driver',
        };
    }

    const currentPickup = pickups.find(
        (pickup) => String(pickup.id) === String(currentPickupId)
    );
    return {
        assignedPickups: [],
        pickupId: currentPickup && !hasPickupDriver(currentPickup) ? currentPickup.id : null,
        source: 'driver',
    };
};

export const formatRelativeTime = (value, now = Date.now()) => {
    if (!value) return 'Awaiting location';

    const timestamp = typeof value === 'number' ? value : new Date(value).getTime();
    if (!Number.isFinite(timestamp)) return 'Awaiting location';

    const seconds = Math.max(0, Math.floor((now - timestamp) / 1000));
    if (seconds < 20) return 'Just now';
    if (seconds < 60) return `${seconds}s ago`;

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    return `${Math.floor(hours / 24)}d ago`;
};

export const isLocationFresh = (location, now = Date.now(), thresholdMs = 2 * 60 * 1000) => {
    if (!location?.updatedAt) return false;
    const timestamp = typeof location.updatedAt === 'number'
        ? location.updatedAt
        : new Date(location.updatedAt).getTime();

    return Number.isFinite(timestamp) && now - timestamp <= thresholdMs;
};

export const matchesDriverStatusFilter = (
    filter,
    driver,
    assignedPickup,
    location,
    now = Date.now(),
    presence = null
) => {
    if (filter === 'all') return true;
    if (filter === 'online') return isDriverOnline(presence, now);
    if (filter === 'no-location') return !location;

    return filter === getDriverAvailability(driver, assignedPickup);
};

export const upsertPickup = (pickups, pickup) => {
    if (!pickup?.id) return pickups;

    const pickupId = String(pickup.id);
    const sanitizedPickup = sanitizeNaviWhitelabelPickup(pickup);
    if (!sanitizedPickup) {
        return pickups.filter((item) => String(item.id) !== pickupId);
    }

    const index = pickups.findIndex((item) => String(item.id) === String(sanitizedPickup.id));
    const mergedPickup = index === -1 ? sanitizedPickup : {...pickups[index], ...sanitizedPickup};
    const statusId = Number(mergedPickup.delivery_status?.id ?? mergedPickup.delivery_status_id);
    const isTerminal = statusId === 5 || statusId === 6 || mergedPickup.waiting_for_driver_at === null;

    if (isTerminal) {
        return pickups.filter((item) => String(item.id) !== String(sanitizedPickup.id));
    }

    if (index === -1) {
        return sanitizedPickup.waiting_for_driver_at ? [sanitizedPickup, ...pickups] : pickups;
    }

    return pickups.map((item, itemIndex) => itemIndex === index ? mergedPickup : item);
};
